import AddIcon from "@mui/icons-material/Add";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import { SetStateAction, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ProjectApi from "../../../../api/projectApi";
import QuizApi from "../../../../api/quizApi";
import ConfirmationModal from "../../../../components/ConfirmationModal/ConfirmationModal";
import Input from "../../../../components/Input/Input";
import MuiSwitch from "../../../../components/MuiSwitch/MuiSwitch";
import { Project } from "../../../../types/Company";
import {
  LocalizedText,
  ProjectQuizBucket,
  QueuedProjectQuizClone,
  Quiz,
  QuizEvaluationType,
  QuizType,
} from "../../../../types/Quiz";
import s from "../ProjectFormPage.module.css";
import { ProjectFormController } from "../useProjectFormController";
import {
  assertQuizChapters,
  getQuizQuestionCount,
} from "../../../../utils/quizChapters";
import { evaluationTypeLabels } from "../../../SystemQuizzes/systemQuizModel";

type QuizStepProps = {
  controller: ProjectFormController;
  project: Project | null;
  token: string | null;
};

type ClonePanelState = {
  bucketIndex: number;
  selectedSource: Quiz | null;
  isConfidential: boolean;
  isEditable: boolean;
  isScoreShown: boolean;
};

type DeleteProjectQuizState = {
  quizId: string;
  title: string;
} | null;

const getProjectTitleLanguages = (controller: ProjectFormController) =>
  Array.from(
    new Set(controller.projectData.title.map((item) => item.language)),
  );

const getRequiredBucketLanguages = (controller: ProjectFormController) =>
  controller.projectData.title
    .filter((item) => item.text.trim().length > 0)
    .map((item) => item.language);

const createEmptyBucket = (languages: string[]): ProjectQuizBucket => ({
  title: languages.map((language) => ({ language, text: "" })),
  activationTime: 0,
  quizes: [],
});

const getLocalizedValue = (
  values: LocalizedText[] | undefined,
  preferredLanguage: string,
  fallback = "Untitled",
) => {
  const preferred = values?.find(
    (item) => item.language === preferredLanguage && item.text.trim(),
  );
  const firstFilled = values?.find((item) => item.text.trim());
  return preferred?.text || firstFilled?.text || fallback;
};

const getBucketTitle = (bucket: ProjectQuizBucket, language: string) =>
  bucket.title.find((item) => item.language === language)?.text ?? "";

const getProjectFromResponse = (response: unknown): Project | null => {
  if (!response) return null;
  if (typeof response === "object" && "status" in response && response.status !== true) return null;
  if (typeof response === "object" && "project" in response) {
    return (response as { project?: Project | null }).project ?? null;
  }
  return response as Project;
};

const isQueuedProjectQuizClone = (
  quiz: ProjectQuizBucket["quizes"][number],
): quiz is QueuedProjectQuizClone =>
  typeof quiz === "object" && "isQueuedProjectQuizClone" in quiz;

const getQuizId = (quiz: ProjectQuizBucket["quizes"][number]) =>
  typeof quiz === "string" ? quiz : quiz._id;

const getSourceQuizId = (quiz: ProjectQuizBucket["quizes"][number]) => {
  if (typeof quiz === "string") return null;
  if (isQueuedProjectQuizClone(quiz)) return quiz.sourceQuizId;
  if (!quiz.clonedFrom) return quiz._id;
  return typeof quiz.clonedFrom === "string"
    ? quiz.clonedFrom
    : quiz.clonedFrom._id;
};

const createQueuedProjectQuizClone = (
  sourceQuiz: Quiz,
  isConfidential: boolean,
  isEditable: boolean,
  isScoreShown: boolean,
): QueuedProjectQuizClone => ({
  _id: `queued-${sourceQuiz._id}-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2)}`,
  isQueuedProjectQuizClone: true,
  sourceQuiz,
  sourceQuizId: sourceQuiz._id,
  isConfidential,
  isEditable,
  isScoreShown,
});

const getRetakeLabel = (retakeAmount: number) => {
  if (retakeAmount === 0) return "Unlimited retakes";
  if (retakeAmount === 1) return "One retake";
  return `${retakeAmount} retakes`;
};

const getEvaluationLabel = (evaluationType: QuizEvaluationType) =>
  evaluationTypeLabels[evaluationType];

const getQuizTypeLabel = (type?: string) =>
  type ? type.replace(/-/g, " ") : "Unclassified";

const QuizStep = ({ controller, project, token }: QuizStepProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [saving, setSaving] = useState(false);
  const [cloning, setCloning] = useState(false);
  const [movingQuizId, setMovingQuizId] = useState("");
  const [deletingQuizId, setDeletingQuizId] = useState("");
  const [hasUnsavedBucketChanges, setHasUnsavedBucketChanges] =
    useState(false);
  const [lockedQuizIds, setLockedQuizIds] = useState<string[]>([]);
  const [loadingSources, setLoadingSources] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [searchText, setSearchText] = useState("");
  const [systemQuizzes, setSystemQuizzes] = useState<Quiz[]>([]);
  const [projectQuizzes, setProjectQuizzes] = useState<Quiz[]>([]);
  const sourceRequest = useRef(0);
  const [clonePanel, setClonePanel] = useState<ClonePanelState | null>(null);
  const [quizToDelete, setQuizToDelete] =
    useState<DeleteProjectQuizState>(null);
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const buckets = controller.projectData.quizBuckets ?? [];
  const projectTitleLanguages = getProjectTitleLanguages(controller);
  const requiredBucketLanguages = getRequiredBucketLanguages(controller);
  const displayLanguage =
    requiredBucketLanguages[0] || projectTitleLanguages[0] || "en";
  const firstNonEmptyBucketIndex = buckets.findIndex(
    (bucket) => bucket.quizes.length > 0,
  );
  const bucketQuizIdsKey = buckets
    .flatMap((bucket) => bucket.quizes.map((quiz) => getQuizId(quiz)))
    .join("|");
  const projectQuizzesById = useMemo(
    () => new Map(projectQuizzes.map((quiz) => [quiz._id, quiz])),
    [projectQuizzes],
  );
  const companyIdFromPath =
    location.pathname.match(/^\/companies\/([^/]+)\/projects\//)?.[1] ?? "";

  const setBuckets = (nextBuckets: ProjectQuizBucket[]) => {
    controller.setProjectData((previous) => ({
      ...previous,
      quizBuckets: nextBuckets,
    }));
  };

  const reloadProjectBuckets = async (projectId: string) => {
    if (!token) return null;

    const reloadResponse = await ProjectApi.getProjectById(token, projectId);
    const authoritativeProject = getProjectFromResponse(reloadResponse);
    if (!authoritativeProject) return null;

    authoritativeProject.quizBuckets?.forEach((bucket) => {
      bucket.quizes.forEach((quiz) => {
        if (typeof quiz !== "string" && !isQueuedProjectQuizClone(quiz)) {
          assertQuizChapters(quiz.quiz);
        }
      });
    });

    setBuckets(authoritativeProject.quizBuckets ?? []);
    return authoritativeProject;
  };

  const isAttemptLockMessage = (message = "") =>
    /attempt|lock|position/i.test(message);

  const getBucketDisplayTitle = (bucket: ProjectQuizBucket, index: number) =>
    getLocalizedValue(bucket.title, displayLanguage, `Bucket ${index + 1}`);

  const getResolvedProjectQuiz = (
    quiz: ProjectQuizBucket["quizes"][number],
  ) => {
    if (isQueuedProjectQuizClone(quiz)) return quiz.sourceQuiz;
    const quizId = getQuizId(quiz);
    return projectQuizzesById.get(quizId) ?? quiz;
  };

  const getResolvedSourceQuizId = (
    quiz: ProjectQuizBucket["quizes"][number],
  ) => {
    const resolvedQuiz = getResolvedProjectQuiz(quiz);
    return getSourceQuizId(resolvedQuiz);
  };

  const getProjectQuizTitle = (
    quiz: ProjectQuizBucket["quizes"][number],
  ) => {
    const resolvedQuiz = getResolvedProjectQuiz(quiz);
    return typeof resolvedQuiz === "string"
      ? resolvedQuiz
      : getLocalizedValue(resolvedQuiz.title, displayLanguage);
  };

  const bucketHasSourceQuiz = (
    bucket: ProjectQuizBucket,
    sourceQuizId: string | null,
  ) =>
    Boolean(sourceQuizId) &&
    bucket.quizes.some((quiz) => getResolvedSourceQuizId(quiz) === sourceQuizId);

  const getBucketsUsingResolvedSource = (sourceId: string) =>
    buckets
      .map((bucket, index) =>
        bucket.quizes.some((quiz) => getResolvedSourceQuizId(quiz) === sourceId)
          ? index
          : -1,
      )
      .filter((index) => index >= 0);

  const validateBucket = (bucket: ProjectQuizBucket, bucketIndex: number) => {
    if (!Number.isInteger(bucket.activationTime) || bucket.activationTime < 0) {
      return `Bucket ${bucketIndex + 1} activation delay must be a non-negative whole number of days.`;
    }
    const missingLanguage = requiredBucketLanguages.find(
      (language) => !getBucketTitle(bucket, language).trim(),
    );
    if (missingLanguage) {
      return `Bucket ${bucketIndex + 1} title is required for ${missingLanguage}.`;
    }
    return "";
  };

  const saveBuckets = async (nextBuckets: ProjectQuizBucket[]) => {
    if (!project) {
      setBuckets(nextBuckets);
      return;
    }

    if (!token) {
      setErrorMessage("Authentication token is missing. Please sign in again.");
      return;
    }

    setSaving(true);
    setErrorMessage("");
    try {
      const updateResponse = await ProjectApi.update(token, {
        _id: project._id,
        quizBuckets: nextBuckets,
      });

      if (!updateResponse.status) {
        setErrorMessage(
          updateResponse.message || "Failed to save Quiz Buckets.",
        );
        return;
      }

      const authoritativeProject = await reloadProjectBuckets(project._id);
      if (!authoritativeProject) {
        setErrorMessage(
          "Quiz Buckets were saved, but the authoritative Project data could not be reloaded.",
        );
        return;
      }
      setHasUnsavedBucketChanges(false);
    } catch (error) {
      console.error("Error saving Project Quiz Buckets:", error);
      setErrorMessage("Something went wrong while saving Quiz Buckets.");
    } finally {
      setSaving(false);
    }
  };

  const loadSystemQuizzes = async () => {
    if (!token || !clonePanel) return;
    const request = ++sourceRequest.current;

    setLoadingSources(true);
    setErrorMessage("");
    try {
      const response = await QuizApi.getQuizes(token, {
        page: 1,
        limit: 25,
        filters: {
          text: searchText.trim() ? searchText.trim() : undefined,
          type: [QuizType.COMPANIES_DIAGNOSTIC],
        },
      });

      if (request !== sourceRequest.current) return;
      if (response.status) {
        (response.quizes ?? []).forEach((quiz) => assertQuizChapters(quiz.quiz));
        setSystemQuizzes(response.quizes ?? []);
        return;
      }

      setSystemQuizzes([]);
      setErrorMessage(response.message || "Failed to load System Quizzes.");
    } catch (error) {
      if (request !== sourceRequest.current) return;
      console.error("Error loading System Quizzes:", error);
      setSystemQuizzes([]);
      setErrorMessage(
        error instanceof Error ? error.message : "Something went wrong while loading System Quizzes.",
      );
    } finally {
      if (request === sourceRequest.current) setLoadingSources(false);
    }
  };

  useEffect(() => {
    if (!clonePanel) return;
    loadSystemQuizzes();
    return () => {
      sourceRequest.current += 1;
    };
  }, [clonePanel?.bucketIndex, searchText, token]);

  useEffect(() => {
    let isCancelled = false;

    const loadProjectQuizzes = async () => {
      if (!token || !project?._id) {
        setProjectQuizzes([]);
        return;
      }

      try {
        const response = await QuizApi.getQuizes(token, {
          page: 1,
          limit: 200,
          filters: { project: project._id },
        });

        if (isCancelled) return;
        if (response.status) {
          (response.quizes ?? []).forEach((quiz) => assertQuizChapters(quiz.quiz));
          setProjectQuizzes((previous) =>
            Array.from(new Map([
              ...previous.map((quiz) => [quiz._id, quiz] as const),
              ...(response.quizes ?? []).map((quiz) => [quiz._id, quiz] as const),
            ]).values()),
          );
          return;
        }

        setErrorMessage(response.message || "Failed to load Project Quizzes.");
      } catch (error) {
        if (!isCancelled) {
          console.error("Error loading Project Quizzes:", error);
          setErrorMessage(error instanceof Error ? error.message : "Failed to load Project Quizzes.");
        }
      }
    };

    loadProjectQuizzes();

    return () => {
      isCancelled = true;
    };
  }, [bucketQuizIdsKey, project?._id, token]);

  useEffect(() => {
    try {
      buckets.forEach((bucket) => {
        bucket.quizes.forEach((quiz) => {
          if (typeof quiz !== "string") {
            assertQuizChapters(
              isQueuedProjectQuizClone(quiz) ? quiz.sourceQuiz.quiz : quiz.quiz,
            );
          }
        });
      });
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "Invalid Project Quiz response.");
    }
  }, [buckets]);

  const addBucket = (insertIndex: number) => {
    const nextBuckets = [...buckets];
    nextBuckets.splice(
      insertIndex,
      0,
      createEmptyBucket(projectTitleLanguages),
    );
    setBuckets(nextBuckets);
    setHasUnsavedBucketChanges(Boolean(project));
    setErrorMessage("");
  };

  const updateBucket = (
    bucketIndex: number,
    updater: (bucket: ProjectQuizBucket) => ProjectQuizBucket,
  ) => {
    setBuckets(
      buckets.map((bucket, index) =>
        index === bucketIndex ? updater(bucket) : bucket,
      ),
    );
    setHasUnsavedBucketChanges(Boolean(project));
    setErrorMessage("");
  };

  const persistUnsavedBucketsBeforeClone = async (
    bucketIndex: number,
    nextBuckets: ProjectQuizBucket[],
  ) => {
    if (!project || !hasUnsavedBucketChanges) return true;

    const validationError = validateBucket(nextBuckets[bucketIndex], bucketIndex);
    if (validationError) {
      setErrorMessage(validationError);
      return false;
    }

    const updateResponse = await ProjectApi.update(token!, {
      _id: project._id,
      quizBuckets: nextBuckets,
    });

    if (!updateResponse.status) {
      setErrorMessage(
        updateResponse.message ||
          "Failed to save Quiz Buckets before cloning the System Quiz.",
      );
      return false;
    }

    setHasUnsavedBucketChanges(false);
    return true;
  };

  const saveBucket = async (bucketIndex: number) => {
    const bucket = buckets[bucketIndex];
    const validationError = validateBucket(bucket, bucketIndex);
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    await saveBuckets(buckets);
  };

  const deleteBucket = async (bucketIndex: number) => {
    const bucket = buckets[bucketIndex];
    if (bucket.quizes.length) return;
    await saveBuckets(buckets.filter((_, index) => index !== bucketIndex));
  };

  const openClonePanel = (bucketIndex: number) => {
    setClonePanel({
      bucketIndex,
      selectedSource: null,
      isConfidential: false,
      isEditable: true,
      isScoreShown: true,
    });
    setSearchText("");
    setSystemQuizzes([]);
    setErrorMessage("");
  };

  const selectSourceQuiz = (source: Quiz) => {
    setClonePanel((previous) =>
      previous
        ? {
            ...previous,
            selectedSource: source,
            isConfidential: source.isConfidential ?? false,
            isEditable: source.isEditable ?? true,
            isScoreShown: source.isScoreShown ?? true,
          }
        : previous,
    );
  };

  const closeClonePanel = () => {
    setClonePanel(null);
    setSearchText("");
    setSystemQuizzes([]);
  };

  const cloneSelectedQuiz = async () => {
    if (!clonePanel?.selectedSource || !token) {
      setErrorMessage("Select a System Quiz and make sure you are signed in.");
      return;
    }

    const destinationBucket = buckets[clonePanel.bucketIndex];
    if (!destinationBucket) {
      setErrorMessage("Destination bucket no longer exists.");
      return;
    }

    const sourceId = clonePanel.selectedSource._id;
    const destinationAlreadyUsesSource = destinationBucket.quizes.some(
      (quiz) => getResolvedSourceQuizId(quiz) === sourceId,
    );
    if (destinationAlreadyUsesSource) {
      setErrorMessage(
        "This bucket already contains a Project Quiz cloned from that System Quiz.",
      );
      return;
    }

    if (!project) {
      updateBucket(clonePanel.bucketIndex, (previous) => ({
        ...previous,
        quizes: [
          ...previous.quizes,
          createQueuedProjectQuizClone(
            clonePanel.selectedSource!,
            clonePanel.isConfidential,
            clonePanel.isEditable,
            clonePanel.isScoreShown,
          ),
        ],
      }));
      closeClonePanel();
      return;
    }

    setCloning(true);
    setErrorMessage("");
    try {
      const bucketsPersisted = await persistUnsavedBucketsBeforeClone(
        clonePanel.bucketIndex,
        buckets,
      );
      if (!bucketsPersisted) return;

      const response = await QuizApi.cloneQuiz(token, {
        quizId: sourceId,
        refs: { project: project._id },
        placement: {
          bucketIndex: clonePanel.bucketIndex,
          quizIndex: destinationBucket.quizes.length,
        },
        projectConfig: {
          isConfidential: clonePanel.isConfidential,
          isEditable: clonePanel.isEditable,
          isScoreShown: clonePanel.isScoreShown,
        },
      });

      if (!response.status) {
        setErrorMessage(response.message || "Failed to clone System Quiz.");
        return;
      }

      if (!response.quiz) {
        throw new Error("Quiz was cloned, but the backend did not return the authoritative Quiz. Reload the Project before retrying.");
      }
      assertQuizChapters(response.quiz.quiz);
      const clonedQuiz = response.quiz;
      setProjectQuizzes((previous) => [
        ...previous.filter((quiz) => quiz._id !== clonedQuiz._id),
        clonedQuiz,
      ]);

      const authoritativeProject = await reloadProjectBuckets(project._id);
      if (!authoritativeProject) {
        setErrorMessage(
          "System Quiz was cloned, but the authoritative Project data could not be reloaded.",
        );
        return;
      }
      closeClonePanel();
    } catch (error) {
      console.error("Error cloning System Quiz into bucket:", error);
      setErrorMessage(error instanceof Error ? error.message : "Something went wrong while cloning the System Quiz.");
    } finally {
      setCloning(false);
    }
  };

  const moveProjectQuiz = async (
    quizId: string,
    placement: { bucketIndex: number; quizIndex: number },
  ) => {
    if (!project || !token) {
      setErrorMessage("Save the Project before reordering Project Quizzes.");
      return false;
    }

    setMovingQuizId(quizId);
    setErrorMessage("");
    try {
      const response = await QuizApi.moveProjectQuiz(token, {
        projectId: project._id,
        quizId,
        placement,
      });

      if (!response.status) {
        const message = response.message || "Failed to move Project Quiz.";
        if (isAttemptLockMessage(message)) {
          setLockedQuizIds((previous) =>
            previous.includes(quizId) ? previous : [...previous, quizId],
          );
          setErrorMessage(
            "This Project Quiz position is locked because an employee has already attempted it. Project data was refreshed.",
          );
          await reloadProjectBuckets(project._id);
          return false;
        }

        setErrorMessage(message);
        return false;
      }

      const authoritativeProject = await reloadProjectBuckets(project._id);
      if (!authoritativeProject) {
        setErrorMessage(
          "Project Quiz was moved, but the authoritative Project data could not be reloaded.",
        );
        return false;
      }
      return true;
    } catch (error) {
      console.error("Error moving Project Quiz:", error);
      setErrorMessage("Something went wrong while moving the Project Quiz.");
      return false;
    } finally {
      setMovingQuizId("");
    }
  };

  const moveQuizWithinBucket = async (
    bucketIndex: number,
    quizIndex: number,
    direction: -1 | 1,
  ) => {
    const bucket = buckets[bucketIndex];
    const quizToMove = bucket?.quizes[quizIndex];
    if (!bucket || !quizToMove) return;

    const quizId = getQuizId(quizToMove);
    const destinationIndex = quizIndex + direction;
    if (
      destinationIndex < 0 ||
      destinationIndex >= bucket.quizes.length ||
      lockedQuizIds.includes(quizId)
    ) {
      return;
    }

    if (!project) {
      const nextBuckets = buckets.map((item, index) => {
        if (index !== bucketIndex) return item;
        const nextQuizes = [...item.quizes];
        const [movedQuiz] = nextQuizes.splice(quizIndex, 1);
        nextQuizes.splice(destinationIndex, 0, movedQuiz);
        return { ...item, quizes: nextQuizes };
      });
      setBuckets(nextBuckets);
      setErrorMessage("");
      return;
    }

    await moveProjectQuiz(quizId, {
      bucketIndex,
      quizIndex: destinationIndex,
    });
  };

  const moveQuizToBucket = async (
    currentBucketIndex: number,
    quizIndex: number,
    destinationBucketIndex: number,
  ) => {
    const currentBucket = buckets[currentBucketIndex];
    const destinationBucket = buckets[destinationBucketIndex];
    const quizToMove = currentBucket?.quizes[quizIndex];
    if (!currentBucket || !destinationBucket || !quizToMove) return;

    const quizId = getQuizId(quizToMove);
    if (
      currentBucketIndex === destinationBucketIndex ||
      lockedQuizIds.includes(quizId)
    ) {
      return;
    }

    const sourceQuizId = getResolvedSourceQuizId(quizToMove);
    if (bucketHasSourceQuiz(destinationBucket, sourceQuizId)) {
      setErrorMessage(
        "The destination bucket already contains a Project Quiz cloned from the same System Quiz.",
      );
      return;
    }

    if (!project) {
      const nextBuckets = buckets.map((bucketItem, index) => {
        if (index === currentBucketIndex) {
          return {
            ...bucketItem,
            quizes: bucketItem.quizes.filter((_, index) => index !== quizIndex),
          };
        }
        if (index === destinationBucketIndex) {
          return {
            ...bucketItem,
            quizes: [...bucketItem.quizes, quizToMove],
          };
        }
        return bucketItem;
      });
      setBuckets(nextBuckets);
      setErrorMessage("");
      return;
    }

    await moveProjectQuiz(quizId, {
      bucketIndex: destinationBucketIndex,
      quizIndex: destinationBucket.quizes.length,
    });
  };

  const openDeleteProjectQuizConfirmation = (
    quiz: ProjectQuizBucket["quizes"][number],
  ) => {
    setQuizToDelete({
      quizId: getQuizId(quiz),
      title: getProjectQuizTitle(quiz),
    });
    setDeleteConfirmVisible(true);
    setErrorMessage("");
  };

  const deleteLocalQueuedProjectQuiz = (quizId: string) => {
    setBuckets(
      buckets.map((bucket) => ({
        ...bucket,
        quizes: bucket.quizes.filter((quiz) => getQuizId(quiz) !== quizId),
      })),
    );
    setDeleteConfirmVisible(false);
    setQuizToDelete(null);
    setErrorMessage("");
  };

  const deleteProjectQuiz = async () => {
    if (!quizToDelete) return;

    if (!project || !token) {
      deleteLocalQueuedProjectQuiz(quizToDelete.quizId);
      return;
    }

    setDeletingQuizId(quizToDelete.quizId);
    setErrorMessage("");
    try {
      const response = await QuizApi.deleteQuiz(token, quizToDelete.quizId);

      if (!response.status) {
        setErrorMessage(response.message || "Failed to delete Project Quiz.");
        setDeleteConfirmVisible(false);
        setQuizToDelete(null);
        return;
      }

      const authoritativeProject = await reloadProjectBuckets(project._id);
      if (!authoritativeProject) {
        setErrorMessage(
          "Project Quiz was deleted, but the authoritative Project data could not be reloaded.",
        );
        return;
      }

      setDeleteConfirmVisible(false);
      setQuizToDelete(null);
    } catch (error) {
      console.error("Error deleting Project Quiz:", error);
      setErrorMessage("Something went wrong while deleting the Project Quiz.");
      setDeleteConfirmVisible(false);
      setQuizToDelete(null);
    } finally {
      setDeletingQuizId("");
    }
  };

  const editProjectQuiz = (
    quiz: ProjectQuizBucket["quizes"][number],
    bucketIndex: number,
  ) => {
    if (!project || isQueuedProjectQuizClone(quiz)) return;

    const searchParams = new URLSearchParams({
      context: "project-quiz",
      projectId: project._id,
      bucketIndex: String(bucketIndex),
      projectTitle: getLocalizedValue(
        project.title,
        displayLanguage,
        "Project",
      ),
      bucketTitle: getBucketDisplayTitle(buckets[bucketIndex], bucketIndex),
    });
    if (companyIdFromPath) {
      searchParams.set("companyId", companyIdFromPath);
    }

    navigate(
      `/system-quizzes/${getQuizId(quiz)}/edit?${searchParams.toString()}`,
    );
  };

  const setProjectQuizDeleteModalVisible = (
    visible: SetStateAction<boolean>,
  ) => {
    setDeleteConfirmVisible((previous) => {
      const nextVisible =
        typeof visible === "function" ? visible(previous) : visible;
      if (!nextVisible) setQuizToDelete(null);
      return nextVisible;
    });
  };

  const renderClonePanel = () => {
    if (!clonePanel) return null;

    const destinationBucket = buckets[clonePanel.bucketIndex];
    if (!destinationBucket) return null;

    return (
      <aside className={s.clonePanel} aria-label="Add System Quiz to bucket">
        <div className={s.clonePanelHeader}>
          <div>
            <span className={s.bucketEyebrow}>
              Bucket {clonePanel.bucketIndex + 1}
            </span>
            <h3>Add System Quiz</h3>
            <p>
              {getLocalizedValue(
                destinationBucket.title,
                displayLanguage,
                "Untitled bucket",
              )}
            </p>
          </div>
          <button
            type="button"
            className={s.iconButton}
            onClick={closeClonePanel}
            aria-label="Close System Quiz panel"
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>

        {clonePanel.selectedSource ? (
          <div className={s.cloneReview}>
            <button
              type="button"
              className={s.bucketSecondaryButton}
              onClick={() =>
                setClonePanel((previous) =>
                  previous ? { ...previous, selectedSource: null } : previous,
                )
              }
              disabled={cloning}
            >
              Back to search
            </button>

            <div className={s.cloneReviewCard}>
              <span className={s.bucketEyebrow}>Selected source</span>
              <h4>
                {getLocalizedValue(
                  clonePanel.selectedSource.title,
                  displayLanguage,
                )}
              </h4>
              <p>
                {getLocalizedValue(
                  clonePanel.selectedSource.description,
                  displayLanguage,
                  "No description",
                )}
              </p>
            </div>

            <div className={s.cloneReviewGrid}>
              <div className={s.cloneMetaBlock}>
                <span>Destination</span>
                <strong>Bucket {clonePanel.bucketIndex + 1}</strong>
              </div>
              <div className={s.cloneMetaBlock}>
                <span>Placement</span>
                <strong>Position {destinationBucket.quizes.length + 1}</strong>
              </div>
              <div className={s.cloneMetaBlock}>
                <span>Inherited confidentiality</span>
                <strong>
                  {clonePanel.selectedSource.isConfidential
                    ? "Confidential"
                    : "Visible"}
                </strong>
              </div>
              <div className={s.cloneMetaBlock}>
                <span>Inherited editability</span>
                <strong>
                  {clonePanel.selectedSource.isEditable ? "Editable" : "Locked"}
                </strong>
              </div>
              <div className={s.cloneMetaBlock}>
                <span>Inherited score visibility</span>
                <strong>
                  {clonePanel.selectedSource.isScoreShown ?? true
                    ? "Shown"
                    : "Hidden"}
                </strong>
              </div>
            </div>

            <div className={s.cloneSwitchList}>
              <MuiSwitch
                lable="Confidential Project Quiz"
                checked={clonePanel.isConfidential}
                setChecked={(isConfidential) =>
                  setClonePanel((previous) =>
                    previous ? { ...previous, isConfidential } : previous,
                  )
                }
              />
              <MuiSwitch
                lable="Editable by Project managers"
                checked={clonePanel.isEditable}
                setChecked={(isEditable) =>
                  setClonePanel((previous) =>
                    previous ? { ...previous, isEditable } : previous,
                  )
                }
              />
              <MuiSwitch
                lable="Show score to participants"
                checked={clonePanel.isScoreShown}
                setChecked={(isScoreShown) =>
                  setClonePanel((previous) =>
                    previous ? { ...previous, isScoreShown } : previous,
                  )
                }
              />
            </div>

            <p className={s.cloneSnapshotNote}>
              Cloning creates a separate Project Quiz. Questions, evaluation,
              interpretations, type, confidentiality, editability, and score
              visibility are
              snapshotted now; later System Quiz edits will not update this
              Project Quiz.
            </p>

            <button
              type="button"
              className={s.bucketPrimaryButton}
              onClick={cloneSelectedQuiz}
              disabled={cloning}
            >
              {cloning ? "Cloning..." : "Clone into bucket"}
            </button>
          </div>
        ) : (
          <>
            <div className={s.cloneSearchRow}>
              <Input
                inputValue={searchText}
                onChangeInput={setSearchText}
                placeholder="Search title or description"
                ariaLabel="Search System Quizzes"
                isSearchinput
              />
              <button
                type="button"
                className={s.iconButton}
                onClick={loadSystemQuizzes}
                disabled={loadingSources}
                aria-label="Search System Quizzes"
              >
                <SearchIcon fontSize="small" />
              </button>
            </div>

            <div className={s.cloneResults}>
              {loadingSources ? (
                <div className={s.cloneEmptyState}>
                  Loading System Quizzes...
                </div>
              ) : systemQuizzes.length ? (
                systemQuizzes.map((source) => {
                    const usedBucketIndexes = getBucketsUsingResolvedSource(
                      source._id,
                    );
                  const disabled = usedBucketIndexes.includes(
                    clonePanel.bucketIndex,
                  );

                  return (
                    <button
                      type="button"
                      key={source._id}
                      className={s.cloneResultCard}
                      onClick={() => selectSourceQuiz(source)}
                      disabled={disabled}
                    >
                      <div>
                        <span className={s.bucketEyebrow}>
                          {getQuizTypeLabel(source.type)}
                        </span>
                        <h4>
                          {getLocalizedValue(source.title, displayLanguage)}
                        </h4>
                        <p>
                          {getLocalizedValue(
                            source.description,
                            displayLanguage,
                            "No description",
                          )}
                        </p>
                      </div>

                      <div className={s.cloneMetaGrid}>
                        <span>{getEvaluationLabel(source.evaluationType)}</span>
                        <span>{getQuizQuestionCount(source.quiz)} questions</span>
                        <span>{getRetakeLabel(source.quiz.retakeAmount)}</span>
                        <span>
                          {source.isConfidential ? "Confidential" : "Visible"}
                        </span>
                        <span>{source.isEditable ? "Editable" : "Locked"}</span>
                        <span>
                          {source.isScoreShown ?? true
                            ? "Score shown"
                            : "Score hidden"}
                        </span>
                      </div>

                      <div className={s.cloneUsage}>
                        {usedBucketIndexes.length
                          ? `Already used in ${usedBucketIndexes
                              .map((index) => `Bucket ${index + 1}`)
                              .join(", ")}`
                          : "Not used in this Project"}
                      </div>

                      {disabled && (
                        <span className={s.cloneDisabledReason}>
                          Already in this destination bucket
                        </span>
                      )}
                    </button>
                  );
                })
              ) : (
                <div className={s.cloneEmptyState}>No System Quizzes found</div>
              )}
            </div>
          </>
        )}
      </aside>
    );
  };

  return (
    <div className={s.stepPanel}>
      <section className={s.section}>
        <div>
          <h2 className={s.sectionTitle}>Project Quiz Buckets</h2>
          <p className={s.sectionText}>
            Buckets are fixed Project stages. Quizzes in the same bucket are
            available concurrently; a later non-empty bucket waits until every
            quiz in the preceding non-empty bucket has its first submission.
          </p>
        </div>

        {errorMessage && (
          <div className={s.formAlert} role="alert">
            {errorMessage}
          </div>
        )}

        {!buckets.length ? (
          <div className={s.bucketEmptyState}>
            <div>
              <h3>No Quiz Buckets yet</h3>
              <p>
                Add the first bucket to define the first stage for Project
                Quizzes. You can add quizzes to it in the next step.
              </p>
            </div>
            <button
              type="button"
              className={s.bucketPrimaryButton}
              onClick={() => addBucket(0)}
              disabled={saving}
            >
              <AddIcon fontSize="small" />
              Add first bucket
            </button>
          </div>
        ) : (
          <div className={s.bucketList}>
            {buckets.map((bucket, bucketIndex) => {
              const isFirstNonEmpty = bucketIndex === firstNonEmptyBucketIndex;
              const isEmpty = bucket.quizes.length === 0;

              return (
                <article className={s.bucketCard} key={bucketIndex}>
                  <div className={s.bucketHeader}>
                    <div>
                      <span className={s.bucketEyebrow}>
                        Bucket {bucketIndex + 1}
                      </span>
                      <h3>{isEmpty ? "Empty Quiz Bucket" : "Quiz Bucket"}</h3>
                    </div>
                    <div className={s.bucketActions}>
                      <button
                        type="button"
                        className={s.bucketSecondaryButton}
                        onClick={() => addBucket(bucketIndex)}
                        disabled={saving}
                      >
                        <AddIcon fontSize="small" />
                        Add before
                      </button>
                      <button
                        type="button"
                        className={s.bucketSecondaryButton}
                        onClick={() => addBucket(bucketIndex + 1)}
                        disabled={saving}
                      >
                        <AddIcon fontSize="small" />
                        Add after
                      </button>
                    </div>
                  </div>

                  {isEmpty ? (
                    <p className={s.bucketStatus}>
                      This bucket is empty and does not participate in stage
                      activation until it contains a Project Quiz.
                    </p>
                  ) : isFirstNonEmpty ? (
                    <p className={s.bucketStatus}>
                      Activation delay is ignored because this is the first
                      non-empty bucket. Its value remains editable for a future
                      stage.
                    </p>
                  ) : (
                    <p className={s.bucketStatus}>
                      This bucket activates after the preceding non-empty bucket
                      is completed, plus its saved delay.
                    </p>
                  )}

                  <div className={s.bucketFieldGrid}>
                    {projectTitleLanguages.map((language) => (
                      <Input
                        key={language}
                        lable={`Bucket title (${language})`}
                        ariaLabel={`Bucket ${bucketIndex + 1} title (${language})`}
                        inputValue={getBucketTitle(bucket, language)}
                        required={requiredBucketLanguages.includes(language)}
                        onChangeInput={(value) =>
                          updateBucket(bucketIndex, (previous) => ({
                            ...previous,
                            title: projectTitleLanguages.map(
                              (itemLanguage) => ({
                                language: itemLanguage,
                                text:
                                  itemLanguage === language
                                    ? value
                                    : getBucketTitle(previous, itemLanguage),
                              }),
                            ),
                          }))
                        }
                      />
                    ))}
                    <Input
                      lable="Activation delay (days)"
                      ariaLabel={`Bucket ${bucketIndex + 1} activation delay`}
                      type="number"
                      inputValue={String(bucket.activationTime)}
                      required
                      onChangeInput={(value) =>
                        updateBucket(bucketIndex, (previous) => ({
                          ...previous,
                          activationTime: Number(value),
                        }))
                      }
                    />
                  </div>

                  <div className={s.bucketQuizList}>
                    {bucket.quizes.length ? (
                      bucket.quizes.map((quiz, quizIndex) => {
                        const quizId = getQuizId(quiz);
                        const isMoving = movingQuizId === quizId;
                        const isDeleting = deletingQuizId === quizId;
                        const sourceQuizId = getResolvedSourceQuizId(quiz);
                        const isQueued = isQueuedProjectQuizClone(quiz);
                        const quizTitle = getProjectQuizTitle(quiz);
                        const destinationBuckets = buckets.filter(
                          (_, index) => index !== bucketIndex,
                        );
                        const validDestinationBuckets =
                          destinationBuckets.filter(
                            (destinationBucket) =>
                              !bucketHasSourceQuiz(
                                destinationBucket,
                                sourceQuizId,
                              ),
                          );
                        const movementDisabled =
                          Boolean(movingQuizId) ||
                          Boolean(deletingQuizId) ||
                          lockedQuizIds.includes(quizId) ||
                          saving ||
                          cloning;

                        return (
                          <div
                            className={s.bucketQuizPill}
                            key={`${getQuizId(quiz)}-${quizIndex}`}
                          >
                            <span>#{quizIndex + 1}</span>
                            <strong>
                              {quizTitle}
                              {isQueued ? (
                                <span className={s.bucketQueuedBadge}>
                                  Queued for creation
                                </span>
                              ) : null}
                            </strong>
                            <div className={s.bucketQuizActions}>
                              <button
                                type="button"
                                className={s.iconButton}
                                onClick={() =>
                                  moveQuizWithinBucket(
                                    bucketIndex,
                                    quizIndex,
                                    -1,
                                  )
                                }
                                disabled={movementDisabled || quizIndex === 0}
                                aria-label={`Move ${quizTitle} up`}
                                title={
                                  lockedQuizIds.includes(quizId)
                                    ? "Position locked after an employee attempt"
                                    : "Move up"
                                }
                              >
                                <ArrowUpwardIcon fontSize="small" />
                              </button>
                              <button
                                type="button"
                                className={s.iconButton}
                                onClick={() =>
                                  moveQuizWithinBucket(
                                    bucketIndex,
                                    quizIndex,
                                    1,
                                  )
                                }
                                disabled={
                                  movementDisabled ||
                                  quizIndex === bucket.quizes.length - 1
                                }
                                aria-label={`Move ${quizTitle} down`}
                                title={
                                  lockedQuizIds.includes(quizId)
                                    ? "Position locked after an employee attempt"
                                    : "Move down"
                                }
                              >
                                <ArrowDownwardIcon fontSize="small" />
                              </button>
                              {isMoving && (
                                <span className={s.bucketQuizMoving}>
                                  Moving...
                                </span>
                              )}
                              {!isQueued && (
                                <button
                                  type="button"
                                  className={s.iconButton}
                                  onClick={() =>
                                    editProjectQuiz(quiz, bucketIndex)
                                  }
                                  disabled={Boolean(deletingQuizId)}
                                  aria-label={`Edit ${quizTitle} Project Quiz`}
                                  title="Edit Project Quiz clone"
                                >
                                  <EditIcon fontSize="small" />
                                </button>
                              )}
                              <button
                                type="button"
                                className={s.iconButtonDanger}
                                onClick={() =>
                                  openDeleteProjectQuizConfirmation(quiz)
                                }
                                disabled={movementDisabled}
                                aria-label={`Delete ${quizTitle} from Project`}
                                title="Delete Project Quiz placement"
                              >
                                <DeleteIcon fontSize="small" />
                              </button>
                              {isDeleting && (
                                <span className={s.bucketQuizMoving}>
                                  Deleting...
                                </span>
                              )}
                            </div>
                            {destinationBuckets.length > 0 &&
                              validDestinationBuckets.length > 0 && (
                                <label className={s.bucketMoveSelectLabel}>
                                  <span>Move to bucket</span>
                                  <select
                                    className={s.bucketMoveSelect}
                                    aria-label={`Move ${quizTitle} to bucket`}
                                    value=""
                                    disabled={movementDisabled}
                                    onChange={(event) => {
                                      const destinationBucketIndex = Number(
                                        event.target.value,
                                      );
                                      if (
                                        Number.isInteger(destinationBucketIndex)
                                      ) {
                                        moveQuizToBucket(
                                          bucketIndex,
                                          quizIndex,
                                          destinationBucketIndex,
                                        );
                                      }
                                    }}
                                  >
                                    <option value="" disabled>
                                      Select destination
                                    </option>
                                    {buckets.map(
                                      (
                                        destinationBucket,
                                        destinationBucketIndex,
                                      ) => {
                                        if (
                                          destinationBucketIndex === bucketIndex
                                        ) {
                                          return null;
                                        }

                                        const hasCollision =
                                          bucketHasSourceQuiz(
                                            destinationBucket,
                                            sourceQuizId,
                                          );
                                        return (
                                          <option
                                            key={destinationBucketIndex}
                                            value={destinationBucketIndex}
                                            disabled={hasCollision}
                                          >
                                            {getBucketDisplayTitle(
                                              destinationBucket,
                                              destinationBucketIndex,
                                            )}
                                            {hasCollision
                                              ? " - already has this System Quiz"
                                              : ""}
                                          </option>
                                        );
                                      },
                                    )}
                                  </select>
                                </label>
                              )}
                          </div>
                        );
                      })
                    ) : (
                      <span className={s.bucketQuizEmpty}>
                        No Project Quizzes in this bucket
                      </span>
                    )}
                  </div>

                  <div className={s.bucketFooter}>
                    <span>
                      {bucket.quizes.length} Project Quiz
                      {bucket.quizes.length === 1 ? "" : "zes"}
                    </span>
                    <div className={s.bucketActions}>
                      <button
                        type="button"
                        className={s.bucketPrimaryButton}
                        onClick={() => openClonePanel(bucketIndex)}
                        disabled={saving || cloning}
                      >
                        <AddIcon fontSize="small" />
                        Add System Quiz
                      </button>
                      <button
                        type="button"
                        className={s.bucketPrimaryButton}
                        onClick={() => saveBucket(bucketIndex)}
                        disabled={saving}
                      >
                        {saving ? "Saving..." : "Save bucket"}
                      </button>
                      <button
                        type="button"
                        className={s.bucketDangerButton}
                        onClick={() => deleteBucket(bucketIndex)}
                        disabled={saving || !isEmpty}
                        title={
                          isEmpty
                            ? "Delete empty bucket"
                            : "Remove all Project Quizzes before deleting this bucket"
                        }
                      >
                        <DeleteIcon fontSize="small" />
                        Delete empty bucket
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>
      {renderClonePanel()}
      <ConfirmationModal
        confirmFunc={deleteProjectQuiz}
        confirmTitle={`Delete Project Quiz "${quizToDelete?.title ?? "Untitled"}"? This removes the Project Quiz placement from its bucket. Historical attempts remain in the system, and the bucket stays visible even if it becomes empty.`}
        modalVisible={deleteConfirmVisible}
        setModalVisible={setProjectQuizDeleteModalVisible}
        loader={Boolean(deletingQuizId)}
      />
    </div>
  );
};

export default QuizStep;
